import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { uploadToS3, uploadViaApiGateway } from '../utils/s3Utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiFile, FiImage, FiVideo, FiMusic, FiX, FiCheck, FiAlertCircle, FiCopy, FiExternalLink } = FiIcons;

function FileUpload({ onUpload }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const getFileIcon = (file) => {
    // Safely handle missing file or file.type
    const mimeType = file?.type || '';
    const [type] = mimeType.split('/');
    switch (type) {
      case 'image': return FiImage;
      case 'video': return FiVideo;
      case 'audio': return FiMusic;
      default: return FiFile;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyToClipboard = async (text, fileId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(fileId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    const newUploadedFiles = [];

    for (const file of acceptedFiles) {
      const fileId = Date.now() + Math.random();
      try {
        // Update progress
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

        // Choose upload method based on environment
        let result;
        if (process.env.REACT_APP_API_GATEWAY_URL) {
          // Use secure API Gateway (recommended for production)
          result = await uploadViaApiGateway(file, user.s3Folder, user.token);
        } else {
          // Direct S3 upload (for development only)
          result = await uploadToS3(file, user.s3Folder);
        }

        // Simulate progress for demo
        for (let progress = 10; progress <= 100; progress += 10) {
          setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const uploadedFile = {
          id: fileId,
          name: file.name,
          originalName: file.name,
          size: file.size,
          type: file.type,
          url: result.url,
          key: result.key,
          uploadedAt: new Date(),
          status: 'completed',
          metadata: {
            title: file.name.split('.')[0],
            description: '',
            tags: [],
            alt: file.name.split('.')[0]
          }
        };

        newUploadedFiles.push(uploadedFile);
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadProgress(prev => ({ ...prev, [fileId]: -1 })); // -1 indicates error
      }
    }

    setUploadedFiles(newUploadedFiles);
    onUpload(newUploadedFiles);
    setUploading(false);

    // Clear progress after 5 seconds (increased to give users time to see URLs)
    setTimeout(() => {
      setUploadProgress({});
    }, 5000);
  }, [user.s3Folder, user.token, onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: 100 * 1024 * 1024, // 100MB
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6"
      id="file-upload-section"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Upload Files</h2>
      
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <motion.div
          animate={{ scale: isDragActive ? 1.1 : 1 }}
          className="flex flex-col items-center space-y-4"
        >
          <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
            isDragActive ? 'bg-blue-100' : 'bg-gray-100'
          }`}>
            <SafeIcon icon={FiUpload} className={`w-8 h-8 transition-colors ${
              isDragActive ? 'text-blue-600' : 'text-gray-600'
            }`} />
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900">
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-gray-500 mt-1">
              or <span className="text-blue-600 font-medium">browse files</span>
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Support for images, videos, documents (max 100MB each)
            </p>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {Object.keys(uploadProgress).length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 space-y-3"
          >
            <h3 className="text-lg font-medium text-gray-900">Upload Results</h3>
            {Object.entries(uploadProgress).map(([fileId, progress]) => {
              const file = uploadedFiles.find(f => f.id.toString() === fileId);
              const fileName = file?.name || `File ${fileId}`;
              
              return (
                <motion.div
                  key={fileId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-gray-50 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={getFileIcon(file || {})} className="w-5 h-5 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">{fileName}</span>
                    </div>
                    {progress === 100 ? (
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                    ) : progress === -1 ? (
                      <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600" />
                    ) : (
                      <span className="text-sm text-gray-600">{progress}%</span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ 
                        width: progress === -1 ? '100%' : `${progress}%`,
                        backgroundColor: progress === -1 ? '#ef4444' : progress === 100 ? '#10b981' : '#3b82f6'
                      }}
                      className="h-2 rounded-full transition-colors"
                    />
                  </div>

                  {/* Show URL when upload is complete */}
                  {progress === 100 && file?.url && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-green-50 border border-green-200 rounded-lg p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-green-600 font-medium mb-1">✅ Upload Successful!</p>
                          <p className="text-xs text-gray-600 truncate font-mono">{file.url}</p>
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => copyToClipboard(file.url, fileId)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                            title="Copy URL"
                          >
                            <SafeIcon icon={copiedId === fileId ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                          </motion.button>
                          <motion.a
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-gray-500 hover:text-green-600 rounded hover:bg-green-50 transition-colors"
                            title="Open in new tab"
                          >
                            <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                          </motion.a>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Show error message when upload fails */}
                  {progress === -1 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 rounded-lg p-3"
                    >
                      <p className="text-xs text-red-600">❌ Upload failed. Please try again.</p>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default FileUpload;